import React, { useCallback, useEffect, useRef, useState } from 'react';

const COLOR_MAP = {
  gray: '#9ca3af', red: '#ef4444', blue: '#3b82f6', green: '#22c55e',
  yellow: '#f59e0b', purple: '#8b5cf6', orange: '#f97316',
};

const MAP_THEMES = {
  default: [],
  light: [
    { elementType: 'geometry', stylers: [{ color: '#f5f3f3' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#D8E0E7' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#D8E0E7' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#90DAEE' }] },
    { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ],
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#212121' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  ],
  silver: [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  ],
  night: [
    { elementType: 'geometry', stylers: [{ color: '#0d1117' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#0d1117' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#4a6a78' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1a2228' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#36454F' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0a1520' }] },
    { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#4a6a78' }] },
  ],
  retro: [
    { elementType: 'geometry', stylers: [{ color: '#ebe3cd' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#523735' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f1e6' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f5deb3' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#d2a679' }] },
    { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#b9d3c2' }] },
  ],
};

function getThemeStyles(mapOptions) {
  if (mapOptions.customMapStyles && typeof mapOptions.customMapStyles === 'string' && mapOptions.customMapStyles.trim()) {
    try { return JSON.parse(mapOptions.customMapStyles); } catch (_) { return []; }
  }
  return MAP_THEMES[mapOptions.mapTheme] || [];
}

// ── Build marker DOM content ──────────────────────────────────────────────────
function buildMarkerContent({ fill, selected, type, title, showLabel, labelOpts }) {
  const sz = selected ? 13 : 9;
  const bw = selected ? '3px' : '2px';
  const shadow = '0 2px 8px rgba(0,0,0,0.5)';

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:absolute;transform:translate(-50%,-100%);cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:3px;user-select:none;';

  // ── Name label above pin ──
  if (showLabel && title) {
    const lbl = document.createElement('div');
    lbl.style.cssText = [
      `background:${labelOpts.bg}`,
      `color:${labelOpts.color}`,
      `font-size:${labelOpts.fontSize}px`,
      'font-weight:600',
      'font-family:system-ui,sans-serif',
      'padding:2px 8px',
      `border-radius:${labelOpts.borderRadius}px`,
      'border:1px solid rgba(255,255,255,0.18)',
      'box-shadow:0 1px 5px rgba(0,0,0,0.3)',
      'white-space:nowrap',
      'max-width:140px',
      'overflow:hidden',
      'text-overflow:ellipsis',
      'pointer-events:none',
    ].join(';');
    lbl.textContent = title;
    wrapper.appendChild(lbl);
  }

  // ── Pin shape ──
  if (type === 'drop') {
    const pin = document.createElement('div');
    pin.style.cssText = 'display:flex;flex-direction:column;align-items:center;';
    const head = document.createElement('div');
    head.style.cssText = `width:${sz * 2}px;height:${sz * 2}px;background:${fill};border-radius:50%;border:${bw} solid #fff;box-shadow:${shadow};`;
    const tip = document.createElement('div');
    tip.style.cssText = `width:0;height:0;border-left:${Math.round(sz * 0.7)}px solid transparent;border-right:${Math.round(sz * 0.7)}px solid transparent;border-top:${sz}px solid ${fill};margin-top:-3px;`;
    pin.appendChild(head);
    pin.appendChild(tip);
    wrapper.appendChild(pin);
  } else if (type === 'arrow') {
    const pin = document.createElement('div');
    pin.style.cssText = `width:0;height:0;border-left:${sz}px solid transparent;border-right:${sz}px solid transparent;border-top:${Math.round(sz * 1.8)}px solid ${fill};filter:drop-shadow(0 2px 4px rgba(0,0,0,0.45));`;
    wrapper.appendChild(pin);
  } else {
    // circle (default)
    const pin = document.createElement('div');
    pin.style.cssText = `width:${sz * 2}px;height:${sz * 2}px;background:${fill};border-radius:50%;border:${bw} solid #fff;box-shadow:${shadow};`;
    wrapper.appendChild(pin);
  }

  return wrapper;
}

// ── Custom OverlayView marker factory ─────────────────────────────────────────
function createMarkerOverlay({ position, color, selected, type, title, showLabel, labelOpts, onClick }) {
  class MarkerOverlay extends window.google.maps.OverlayView {
    constructor() {
      super();
      this.div = null;
      this._pos = new window.google.maps.LatLng(position.lat, position.lng);
      this._opts = { color, selected, type, title, showLabel, labelOpts };
    }

    _buildEl() {
      const fill = COLOR_MAP[this._opts.color] || this._opts.color || '#9ca3af';
      const el = buildMarkerContent({ fill, ...this._opts });
      el.style.zIndex = this._opts.selected ? '100' : '10';
      el.addEventListener('click', e => { e.stopPropagation(); onClick && onClick(); });
      return el;
    }

    onAdd() {
      this.div = this._buildEl();
      this.getPanes().overlayMouseTarget.appendChild(this.div);
    }

    draw() {
      const pt = this.getProjection().fromLatLngToDivPixel(this._pos);
      if (pt && this.div) { this.div.style.left = pt.x + 'px'; this.div.style.top = pt.y + 'px'; }
    }

    onRemove() {
      if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
      this.div = null;
    }

    getPosition() { return this._pos; }

    update(newOpts) {
      Object.assign(this._opts, newOpts);
      if (!this.div) return;
      const newEl = this._buildEl();
      if (this.div.parentNode) this.div.parentNode.replaceChild(newEl, this.div);
      this.div = newEl;
      this.draw();
    }
  }

  return new MarkerOverlay();
}

function generateMapStyles(options) {
  const styles = [...getThemeStyles(options)];
  if (!options.showRoadNames) styles.push({ featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] });
  if (!options.showPoi) styles.push({ featureType: 'poi', stylers: [{ visibility: 'off' }] });
  if (!options.showTransit) styles.push({ featureType: 'transit', stylers: [{ visibility: 'off' }] });
  if (options.showWaterBodies === false)
    styles.push({ featureType: 'water', stylers: [{ visibility: 'off' }] });
  return styles;
}

export default function GoogleMapWidget({
  apiKey = '',
  startingLat = 40.7128,
  startingLng = -74.0060,
  markers = [],
  selectedMarkerIndex = null,
  onMarkerSelect = () => { },
  zoom = 12,
  showInfoCard = true,
  mapOptions = {},
  centerLocation = null,
  showRoutes = false,
  infoCardGroupId = null,
  infoCardGroup = null,
  renderGroup = null,
  markerColor = 'gray',
  selectedMarkerColor = 'red',
  markerType = 'circle',
  routeColor = '#6366f1',
  routeWeight = 3,
  routeOpacity = 0.7,
  mapTheme = 'default',
  customMapStyles = '',
  centerLabelText = '',
  centerLabelBg = '#1a2228',
  centerLabelColor = '#ffffff',
  centerLabelBorderRadius = 20,
  centerLabelFontSize = 12,
  centerLabelBorderColor = 'rgba(255,255,255,0.2)',
  showMarkerLabel = false,
  markerLabelBg = 'rgba(15,22,28,0.88)',
  markerLabelColor = '#ffffff',
  markerLabelBorderRadius = 10,
  markerLabelFontSize = 10,
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const centerMarkerRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const routeRenderersRef = useRef([]);
  const centerLabelOverlayRef = useRef(null);
  const [mapReady, setMapReady] = useState(!!window.google?.maps);
  const [mapInstanceReady, setMapInstanceReady] = useState(false);
  const [selectedRouteMeta, setSelectedRouteMeta] = useState(null);
  const [error, setError] = useState(null);

  // ── 1. Load Maps API (singleton) ──────────────────────────────────────────
  useEffect(() => {
    if (window.google?.maps) {
      setMapReady(true);
      return;
    }
    if (!apiKey || apiKey.trim() === '') {
      setError('Google Maps API key is required');
      return;
    }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      const timer = setInterval(() => {
        if (window.google?.maps) { setMapReady(true); clearInterval(timer); }
      }, 100);
      return () => clearInterval(timer);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
    script.async = true;
    script.onload = () => setMapReady(true);
    script.onerror = () => setError('Failed to load Google Maps API');
    document.head.appendChild(script);
    // Do NOT remove the script on cleanup — Google Maps is a page-level singleton
  }, [apiKey]);

  // ── 2. Initialise map (runs once after API loads) ─────────────────────────
  useEffect(() => {
    if (!mapReady || !mapContainer.current || !window.google?.maps) return;
    if (map.current) return; // already initialised

    const initCenter = centerLocation
      ? { lat: parseFloat(centerLocation.lat), lng: parseFloat(centerLocation.lng) }
      : { lat: startingLat, lng: startingLng };

    map.current = new window.google.maps.Map(mapContainer.current, {
      zoom: zoom || 5,
      center: initCenter,
      mapTypeId: mapOptions.mapType || 'roadmap',
      mapTypeControl: false,
      fullscreenControl: true,
      zoomControl: true,
      streetViewControl: false,
      scaleControl: false,
      rotateControl: false,
      gestureHandling: 'greedy',   // no Ctrl+scroll needed — important for kiosk
      clickableIcons: false,        // POI clicks don't interrupt
      styles: generateMapStyles(mapOptions),
    });

    // Clicking anywhere on the map (not on a marker) dismisses the info card
    map.current.addListener('click', () => {
      onMarkerSelectRef.current(null);
    });

    setMapInstanceReady(true);

    if (showRoutes) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }
  }, [mapReady]);

  // ── 3. Re-centre when centerLocation changes ───────────────────────────────
  useEffect(() => {
    if (!map.current || !centerLocation?.lat || !centerLocation?.lng) return;
    const pos = { lat: parseFloat(centerLocation.lat), lng: parseFloat(centerLocation.lng) };
    map.current.setCenter(pos);

    // Center marker (blue star)
    if (centerMarkerRef.current) centerMarkerRef.current.setMap(null);
    centerMarkerRef.current = new window.google.maps.Marker({
      position: pos,
      map: map.current,
      title: centerLocation.title || 'Center',
      icon: {
        path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
        scale: 6,
        fillColor: '#3b82f6',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 1.5,
      },
      zIndex: 1000,
    });
  }, [centerLocation]);

  // ── 4. Recreate marker objects when the markers array changes ─────────────
  const onMarkerSelectRef = useRef(onMarkerSelect);
  onMarkerSelectRef.current = onMarkerSelect;

  useEffect(() => {
    if (!mapInstanceReady || !map.current) return;

    // Remove old markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (!Array.isArray(markers) || markers.length === 0) return;

    const labelOpts = { bg: markerLabelBg, color: markerLabelColor, fontSize: markerLabelFontSize, borderRadius: markerLabelBorderRadius };
    markers.forEach((markerData, index) => {
      const lat = parseFloat(markerData.lat);
      const lng = parseFloat(markerData.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      const isSelected = index === selectedMarkerIndex;
      const color = isSelected ? selectedMarkerColor : markerColor;
      const overlay = createMarkerOverlay({
        position: { lat, lng },
        color,
        selected: isSelected,
        type: markerType,
        title: markerData.title || `Location ${index + 1}`,
        showLabel: showMarkerLabel,
        labelOpts,
        onClick: () => onMarkerSelectRef.current(index),
      });
      overlay.setMap(map.current);
      markersRef.current.push(overlay);
    });

    // Auto-fit bounds to show all markers (only when no marker is already selected)
    if (selectedMarkerIndex === null && markers.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(({ lat, lng }) => {
        const la = parseFloat(lat), lo = parseFloat(lng);
        if (!isNaN(la) && !isNaN(lo)) bounds.extend({ lat: la, lng: lo });
      });
      map.current.fitBounds(bounds, 40); // 40px padding
    }
  }, [mapInstanceReady, markers, selectedMarkerIndex, markerColor, selectedMarkerColor, markerType, showMarkerLabel, markerLabelBg, markerLabelColor, markerLabelFontSize, markerLabelBorderRadius]);

  // ── 5. Update overlay style when selection changes (no full rebuild) ──────
  useEffect(() => {
    markersRef.current.forEach((m, index) => {
      const isSelected = index === selectedMarkerIndex;
      m.update({ color: isSelected ? selectedMarkerColor : markerColor, selected: isSelected });
    });
    // Pan to selected marker
    if (selectedMarkerIndex !== null && markersRef.current[selectedMarkerIndex]) {
      const pos = markersRef.current[selectedMarkerIndex].getPosition();
      map.current?.panTo(pos);
      map.current?.setZoom(Math.max(map.current.getZoom(), 14));
    }
  }, [selectedMarkerIndex, markerColor, selectedMarkerColor]);

  // ── Label overlay ("You are here") ──────────────────────────────────────────
  useEffect(() => {
    if (centerLabelOverlayRef.current) {
      centerLabelOverlayRef.current.setMap(null);
      centerLabelOverlayRef.current = null;
    }
    if (!mapInstanceReady || !map.current || !centerLocation?.lat || !centerLocation?.lng || !centerLabelText) return;

    class YouAreHereOverlay extends window.google.maps.OverlayView {
      constructor(pos, opts) { super(); this.pos = pos; this.opts = opts; this.div = null; }
      onAdd() {
        const div = document.createElement('div');
        div.style.cssText = 'position:absolute;pointer-events:none;white-space:nowrap;';
        div.style.transform = 'translate(-50%, calc(-100% - 18px))';
        div.style.background = this.opts.bg;
        div.style.color = this.opts.color;
        div.style.fontSize = this.opts.fontSize + 'px';
        div.style.fontWeight = '600';
        div.style.fontFamily = 'system-ui,sans-serif';
        div.style.padding = '4px 10px';
        div.style.borderRadius = this.opts.borderRadius + 'px';
        div.style.boxShadow = '0 2px 8px rgba(0,0,0,0.35)';
        div.style.border = '1.5px solid ' + this.opts.borderColor;
        div.textContent = this.opts.text;
        const arrow = document.createElement('div');
        arrow.style.cssText = 'position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;';
        arrow.style.borderTop = '6px solid ' + this.opts.bg;
        div.appendChild(arrow);
        this.div = div;
        this.getPanes().overlayMouseTarget.appendChild(div);
      }
      draw() {
        const pt = this.getProjection().fromLatLngToDivPixel(this.pos);
        if (pt && this.div) { this.div.style.left = pt.x + 'px'; this.div.style.top = pt.y + 'px'; }
      }
      onRemove() {
        if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
        this.div = null;
      }
    }
    const pos = new window.google.maps.LatLng(parseFloat(centerLocation.lat), parseFloat(centerLocation.lng));
    const overlay = new YouAreHereOverlay(pos, {
      text: centerLabelText, bg: centerLabelBg, color: centerLabelColor,
      borderRadius: centerLabelBorderRadius, fontSize: centerLabelFontSize, borderColor: centerLabelBorderColor,
    });
    overlay.setMap(map.current);
    centerLabelOverlayRef.current = overlay;
  }, [mapInstanceReady, centerLocation, centerLabelText, centerLabelBg, centerLabelColor, centerLabelBorderRadius, centerLabelFontSize, centerLabelBorderColor]);

  // ── 6. Draw / clear routes ─────────────────────────────────────────────────
  useEffect(() => {
    if (!map.current) return;

    map.current.setOptions({
      mapTypeId: mapOptions.mapType || 'roadmap',
      styles: generateMapStyles(mapOptions),
    });
  }, [mapOptions]);

  useEffect(() => {
    if (!map.current || typeof zoom !== 'number') return;
    map.current.setZoom(zoom);
  }, [zoom]);

  useEffect(() => {
    routeRenderersRef.current.forEach(r => r.setMap(null));
    routeRenderersRef.current = [];
    setSelectedRouteMeta(null);

    if (!mapInstanceReady || !showRoutes || !centerLocation?.lat || !centerLocation?.lng) return;
    if (selectedMarkerIndex === null || selectedMarkerIndex === undefined) return;

    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }

    const origin = { lat: parseFloat(centerLocation.lat), lng: parseFloat(centerLocation.lng) };
    const selected = markers[selectedMarkerIndex];
    if (!selected) return;

    const lat = parseFloat(selected.lat);
    const lng = parseFloat(selected.lng);
    if (isNaN(lat) || isNaN(lng)) return;

    directionsServiceRef.current.route(
      { origin, destination: { lat, lng }, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status !== 'OK' || !map.current) return;

        const leg = result?.routes?.[0]?.legs?.[0];
        if (leg) {
          setSelectedRouteMeta({
            distance: leg.distance?.text || '',
            time: leg.duration?.text || '',
            distanceText: leg.distance?.text || '',
            timeText: leg.duration?.text || '',
            routeDistance: leg.distance?.text || '',
            routeTime: leg.duration?.text || '',
            distanceMeters: leg.distance?.value ?? null,
            timeSeconds: leg.duration?.value ?? null,
            durationInTraffic: leg.duration_in_traffic?.text || '',
          });
        }

        const r = new window.google.maps.DirectionsRenderer({
          map: map.current,
          directions: result,
          suppressMarkers: true,
          polylineOptions: { strokeColor: routeColor, strokeWeight: routeWeight, strokeOpacity: routeOpacity },
        });
        routeRenderersRef.current.push(r);
      },
    );
  }, [mapInstanceReady, showRoutes, centerLocation, markers, selectedMarkerIndex, routeColor, routeWeight, routeOpacity]);

  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#2a363d',
        color: '#ef4444',
        fontSize: 12,
        padding: 12,
        textAlign: 'center',
      }}>
        {error}
      </div>
    );
  }

  const selectedMarker = selectedMarkerIndex !== null ? markers[selectedMarkerIndex] : null;
  const selectedMarkerDataWithRoute = selectedMarker
    ? {
      ...(selectedMarker.data || {}),
      distance: selectedRouteMeta?.distance || '',
      time: selectedRouteMeta?.time || '',
      distanceText: selectedRouteMeta?.distanceText || '',
      timeText: selectedRouteMeta?.timeText || '',
      routeDistance: selectedRouteMeta?.routeDistance || '',
      routeTime: selectedRouteMeta?.routeTime || '',
      distanceMeters: selectedRouteMeta?.distanceMeters,
      timeSeconds: selectedRouteMeta?.timeSeconds,
      durationInTraffic: selectedRouteMeta?.durationInTraffic || '',
    }
    : null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* Map Container */}
      <div
        ref={mapContainer}
        style={{
          width: '100%',
          height: '100%',
          background: '#e5e3df',
        }}
      />

      {showInfoCard && selectedMarker && (
        <div style={{
          position: 'absolute',
          left: infoCardGroup?.styles?.x || '50%',
          top: infoCardGroup?.styles?.y || '50%',
          transform: infoCardGroup ? 'none' : 'translate(-50%, -50%)',
          zIndex: 1000,
          pointerEvents: 'none',
        }}>
          {infoCardGroup && renderGroup ? (
            // Render linked custom group with marker data — use the group's own dimensions
            <div style={{
              width: infoCardGroup.styles?.width || 'auto',
              height: infoCardGroup.styles?.height || 'auto',
              overflow: 'hidden',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.6)',
              borderRadius: infoCardGroup.styles?.borderRadius || 8,
              pointerEvents: 'auto',
            }}>
              {renderGroup(infoCardGroup, selectedMarkerDataWithRoute)}
            </div>
          ) : (
            // Fallback default card
            <div style={{
              background: '#2a363d',
              border: '1px solid #36454F',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'auto',
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f0f4f5', marginBottom: 8 }}>
                {selectedMarker.title || 'Location'}
              </div>

              {selectedMarker.data?.image && (
                <img src={selectedMarker.data.image} alt={selectedMarker.title}
                  style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4, marginBottom: 8 }} />
              )}

              <div style={{ fontSize: 11, color: '#8faab4', marginBottom: 8, fontFamily: 'monospace' }}>
                📍 {parseFloat(selectedMarker.lat).toFixed(4)}, {parseFloat(selectedMarker.lng).toFixed(4)}
              </div>

              {selectedMarkerDataWithRoute && (
                <div style={{ fontSize: 11, color: '#8faab4', lineHeight: 1.6 }}>
                  {Object.entries(selectedMarkerDataWithRoute)
                    .filter(([k]) => !['lat', 'lng', 'image'].includes(k))
                    .slice(0, 5)
                    .map(([k, v]) => (
                      <div key={k} style={{ marginBottom: 4 }}>
                        <span style={{ color: '#6366f1', fontWeight: 600 }}>{k}:</span> {String(v)}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {/* No API Key Warning */}
      {!apiKey || apiKey.trim() === '' && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(26, 34, 40, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#f59e0b',
          fontSize: 12,
          padding: 20,
          textAlign: 'center',
          zIndex: 2000,
        }}>
          <div>
            <div style={{ marginBottom: 8 }}>⚠️ Google Maps API Key Required</div>
            <div style={{ fontSize: 11, color: '#8faab4' }}>
              Configure your API key in the editor settings
            </div>
          </div>
        </div>
      )}
    </div>
  );
}